import SYALLABIFICATION_JSON_DATA from '../../constants/MemoryGames/syllabification.json';
import { createI18nInfo } from '../../utils/i18n';
import {
  splitIntoGraphme,
  splitIntoSentence,
  splitIntoWords,
} from '../../utils/string';
import { GameCharacter, GameSoundType, TtsLocale } from '../Base';
import {
  ConcreteGame,
  FingerWritingMode,
  FingerWritingSoundMode,
  FingerWritingVariant,
  FingerWritingWord,
  GameData,
  GameScenario,
  GameType,
  I18nGameData,
  TextOrderingSoundMode,
  TextOrderingUnit,
  TextOrderingUnitType,
  TextOrderingVariant,
  TextOrderingWord,
} from '../GameStore';

import { GameDraft } from './GameDraft';
import { GamePart } from './GamePart';
import { GameProduct } from './GameProduct';

export enum ConvertType {
  FillInTheBlanksBySpelling = 'fillInTheBlanksBySpelling',
  FillInTheBlanksBySyllable = 'fillInTheBlanksBySyllable',
  JoyaPassageBasic = 'joyaPassageBasic',
  JoyaPassage = 'joyaPassage',
  ScriptureBasic = 'scriptureBasic',
  ScriptureStandard = 'scriptureStandard',
  FingerWritingStrokeBySpelling = 'fingerWritingStrokeBySpelling',
  FingerWritingStrokeByComponent = 'fingerWritingStrokeByComponent',
}

export const gameProductConvertFunc = (convertType: ConvertType) => {
  switch (convertType) {
    case ConvertType.FillInTheBlanksBySpelling:
      return convertFillInTheBlanksBySpellingProduct;
    case ConvertType.FillInTheBlanksBySyllable:
      return convertFillInTheBlanksBySyllableProduct;
    case ConvertType.JoyaPassageBasic:
    case ConvertType.ScriptureBasic:
      return convertJoyaPassageBasicProduct;
    case ConvertType.JoyaPassage:
    case ConvertType.ScriptureStandard:
      return convertJoyaPassageProduct;
    case ConvertType.FingerWritingStrokeBySpelling:
      return convertWritingStrokeBySpellingProduct;
    case ConvertType.FingerWritingStrokeByComponent:
      return convertWritingStrokeByComponentProduct;
  }
};

export const createGamePart = (
  convertType: ConvertType,
  index: number,
): GamePart => {
  const partId = String(index).padStart(2, '0');
  switch (convertType) {
    case ConvertType.FillInTheBlanksBySpelling:
      return {
        index,
        partId: partId,
        name: 'spelling',
        gameType: convertType,
        iconTag: 'spelling',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.FillInTheBlanksBySyllable:
      return {
        index,
        partId: partId,
        name: 'syllable',
        gameType: convertType,
        iconTag: 'syllable',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.JoyaPassageBasic:
      return {
        index,
        partId: partId,
        name: 'joyaPassageBasic',
        gameType: convertType,
        iconTag: 'joyaPassageBasic',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.JoyaPassage:
      return {
        index,
        partId: partId,
        name: 'joyaPassage',
        gameType: convertType,
        iconTag: 'joyaPassage',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.ScriptureBasic:
      return {
        index,
        partId: partId,
        name: 'scriptureBasic',
        gameType: convertType,
        iconTag: 'scriptureBasic',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.ScriptureStandard:
      return {
        index,
        partId: partId,
        name: 'scriptureStandard',
        gameType: convertType,
        iconTag: 'scriptureStandard',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.FingerWritingStrokeBySpelling:
      return {
        index,
        partId: partId,
        name: 'fingerWritingStrokeBySpelling',
        gameType: convertType,
        iconTag: 'fingerWritingStrokeBySpelling',
        isRoot: false,
        isSelected: true,
      };
    case ConvertType.FingerWritingStrokeByComponent:
      return {
        index,
        partId: partId,
        name: 'fingerWritingStrokeByComponent',
        gameType: convertType,
        iconTag: 'fingerWritingStrokeByComponent',
        isRoot: false,
        isSelected: true,
      };
  }
};

const generateTextOrderingProduct = (
  gameDraft: GameDraft,
  wordsResult: TextOrderingWord[],
  {
    draftId,
    partId,
    index,
  }: { draftId: string; partId: string; index: number },
  {
    answerFirstLetter = false,
    useColor,
    isForChallenge = true,
  }: {
    answerFirstLetter?: boolean;
    useColor?: boolean;
    isForChallenge?: boolean;
  } = {},
) => {
  const locale = gameDraft.locale;
  const plainText = gameDraft.game.answer.text.text;
  const title = gameDraft.game.question.text.text;

  if (
    wordsResult.every((result) =>
      result.units.every((unit) => unit.type === TextOrderingUnitType.Hint),
    )
  ) {
    return undefined;
  }

  const gameData: GameData<TextOrderingVariant> = {
    title: title.slice(0, 100),
    description: plainText,
    variants: [
      {
        instruction: {
          text: gameDraft.game.question.text.text,
          sound:
            gameDraft.game.question.hasTts &&
            (answerFirstLetter || gameDraft.game.question.text.hasAutoTranslate)
              ? {
                  type: GameSoundType.Tts,
                  locale: gameDraft.game.question.locale,
                  text:
                    gameDraft.game.question.text.soundText ??
                    gameDraft.game.question.text.text,
                }
              : undefined,
        },
        answer: {
          words: wordsResult,
          locale: gameDraft.game.answer.locale,
          soundMode:
            gameDraft.game.question.hasTts && gameDraft.game.answer.hasTts
              ? TextOrderingSoundMode.Normal
              : TextOrderingSoundMode.Silent,
          answerFirstLetter,
          isLyrics: false,
          useColor,
        },
      },
    ],
  };

  const i18nGameData: I18nGameData<TextOrderingVariant> = createI18nInfo(
    locale,
    gameData,
  );

  const game = new ConcreteGame({
    gameId: draftId,
    gameCollectionId: 'gameCollectionId',
    classroomId: 'classroomId',
    createdBy: 'createdBy',
    createdAt: new Date(),
    lastModifiedAt: new Date(),
    version: '1.0',
    timeLimit: 'auto',
    i18nGameData: i18nGameData,
    gameType: GameType.TextOrdering,
    scenario: GameScenario.Default,
    dependencies: {
      images: [],
      audios: [],
      videos: [],
      resourceImages: [],
      strokes: [],
      ttsAudios: [],
    },
  });

  const gameProduct: GameProduct = {
    draftId: draftId,
    partId: partId,
    isSelected: true,
    index: index,
    game: game,
    title: title,
    description: plainText,
    isForChallenge: isForChallenge,
  };

  return gameProduct;
};

const generateFingerWritingProduct = (
  gameDraft: GameDraft,
  wordsResult: FingerWritingWord[],
  {
    draftId,
    partId,
    index,
  }: { draftId: string; partId: string; index: number },
  {
    useComponent = false,
    isForChallenge = true,
  }: { useComponent?: boolean; isForChallenge?: boolean } = {},
) => {
  const locale = gameDraft.locale;
  const plainText = gameDraft.game.answer.text.text;
  const title = gameDraft.game.question.text.text;

  if (
    wordsResult.every((result) =>
      result.characters.every((character) => !character.strokeCount),
    )
  ) {
    return undefined;
  }

  const gameData: GameData<FingerWritingVariant> = {
    title: title.slice(0, 100),
    description: plainText,
    variants: [
      {
        mode: FingerWritingMode.Ordering,
        instruction: {
          text: gameDraft.game.question.text.text,
          sound:
            gameDraft.game.question.hasTts &&
            gameDraft.game.question.text.hasAutoTranslate
              ? {
                  type: GameSoundType.Tts,
                  locale: gameDraft.game.question.locale,
                  text:
                    gameDraft.game.question.text.soundText ??
                    gameDraft.game.question.text.text,
                }
              : undefined,
        },
        answer: {
          words: wordsResult,
          locale: gameDraft.game.answer.locale,
          soundMode:
            gameDraft.game.question.hasTts && gameDraft.game.answer.hasTts
              ? FingerWritingSoundMode.SpeakBeforehand
              : FingerWritingSoundMode.Silent,
          strokeSettings: useComponent ? { useComponent: true } : undefined,
        },
      },
    ],
  };

  const i18nGameData: I18nGameData<FingerWritingVariant> = createI18nInfo(
    locale,
    gameData,
  );

  const game = new ConcreteGame({
    gameId: draftId,
    gameCollectionId: 'gameCollectionId',
    classroomId: 'classroomId',
    createdBy: 'createdBy',
    createdAt: new Date(),
    lastModifiedAt: new Date(),
    version: '1.0',
    timeLimit: 'auto',
    i18nGameData: i18nGameData,
    gameType: GameType.FingerWriting,
    scenario: GameScenario.Default,
    dependencies: {
      images: [],
      audios: [],
      videos: [],
      resourceImages: [],
      strokes: [],
      ttsAudios: [],
    },
  });

  const gameProduct: GameProduct = {
    draftId: draftId,
    partId: partId,
    isSelected: true,
    index: index,
    game: game,
    title: title,
    description: plainText,
    isForChallenge: isForChallenge,
  };

  return gameProduct;
};

const convertFillInTheBlanksBySpellingProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        if (word.type === TextOrderingUnitType.Blank) {
          const graphmes = splitIntoGraphme(word.key, locale);
          graphmes.forEach((graphme) => {
            if (graphme.isWordLike) {
              selectWord.push({
                key: graphme.text,
                type: TextOrderingUnitType.Blank,
              });
            } else {
              selectWord.push({
                key: graphme.text,
                type: TextOrderingUnitType.Hint,
              });
            }
          });
        } else {
          selectWord.push(word);
        }
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(gameDraft, wordsResult, {
    draftId,
    partId,
    index: gameIndex,
  });
};

const convertFillInTheBlanksBySyllableProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  if (
    !([TtsLocale.AU, TtsLocale.GB, TtsLocale.US] as TtsLocale[]).includes(
      gameDraft.locale,
    )
  ) {
    return undefined;
  }
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        if (word.type === TextOrderingUnitType.Blank) {
          const SYALLABIFICATION: Record<string, string> =
            SYALLABIFICATION_JSON_DATA;
          const syllable = SYALLABIFICATION[word.key.toLowerCase()];

          let syllables: string[];
          if (syllable) {
            syllables = [];

            let syllableStartIndex = 0;
            syllable.split('-').forEach((s) => {
              const originalSyllable = word.key.substring(
                syllableStartIndex,
                syllableStartIndex + s.length,
              );
              syllableStartIndex += s.length;
              syllables.push(originalSyllable);
            });
          } else {
            // ref: https://stackoverflow.com/a/49407494
            const syllableRegex =
              /[^aeiouy]*[aeiouy]+(?:[^aeiouy]*$|[^aeiouy](?=[^aeiouy]))?/gi;
            syllables = word.key.match(syllableRegex) ?? [];
          }

          if (syllables.length > 1) {
            syllables.forEach((s) => {
              selectWord.push({
                key: s,
                type: TextOrderingUnitType.Blank,
              });
            });
          } else {
            const graphmes = splitIntoGraphme(word.key, locale);
            if (
              graphmes.length > 1
              // &&
              // graphmes.every((graphme) => graphme.isWordLike)
            ) {
              graphmes.forEach((graphme) => {
                if (graphme.isWordLike) {
                  selectWord.push({
                    key: graphme.text,
                    type: TextOrderingUnitType.Blank,
                  });
                } else {
                  selectWord.push({
                    key: graphme.text,
                    type: TextOrderingUnitType.Hint,
                  });
                }
              });
            } else {
              selectWord.push({
                key: word.key,
                type: TextOrderingUnitType.Hint,
              });
            }
          }
        } else {
          selectWord.push(word);
        }
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(
    gameDraft,
    wordsResult,
    {
      draftId,
      partId,
      index: gameIndex,
    },
    { useColor: true, isForChallenge: false },
  );
};

const convertJoyaPassageBasicProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        selectWord.push(word);
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(
    gameDraft,
    wordsResult,
    {
      draftId,
      partId,
      index: gameIndex,
    },
    {
      answerFirstLetter: true,
      useColor: true,
      isForChallenge: false,
    },
  );
};

const convertJoyaPassageProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: TextOrderingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitWords = splitIntoWords(sentence.text, locale);

    const selectWord: TextOrderingUnit[] = [];
    splitWords.forEach((_, index) => {
      const word = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      if (word) {
        selectWord.push(word);
      }
    });
    startIndex = startIndex + splitWords.length;

    wordsResult.push({ word: sentence.text, units: selectWord });
  });

  return generateTextOrderingProduct(
    gameDraft,
    wordsResult,
    {
      draftId,
      partId,
      index: gameIndex,
    },
    {
      answerFirstLetter: true,
    },
  );
};

const convertWritingStrokeBySpellingProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: FingerWritingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const strokeResolution = gameDraft.game.answer.text.strokeResolution;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitGraphme = splitIntoGraphme(sentence.text, locale, {
      withSpace: true,
    });

    const selectedCharacter: GameCharacter<false>[] = [];
    splitGraphme.forEach((_, index) => {
      const character = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      // export type GameCharacter<B extends boolean> = {
      //   character: string;
      //   ttsAudio?: Uuid;
      //   strokeCount?: number;
      // } & (B extends true ? { fileId: Uuid } : { fileId?: Uuid });

      if (character) {
        const strokeData = strokeResolution?.[character.key];

        if (strokeData && character.type === TextOrderingUnitType.Blank) {
          selectedCharacter.push({
            character: character.key,
            strokeCount: strokeData.strokeCount,
            fileId: strokeData.fileId,
          });
        } else if (strokeData) {
          selectedCharacter.push({
            character: character.key,
            fileId: strokeData.fileId,
          });
        } else {
          selectedCharacter.push({
            character: character.key,
          });
        }
      }
    });
    startIndex = startIndex + splitGraphme.length;

    wordsResult.push({ word: sentence.text, characters: selectedCharacter });
  });

  return generateFingerWritingProduct(gameDraft, wordsResult, {
    draftId,
    partId,
    index: gameIndex,
  });
};

const convertWritingStrokeByComponentProduct = (
  gameDraft: GameDraft,
  selectWords: TextOrderingUnit[],
  {
    draftId,
    partId,
    gameIndex,
  }: { draftId: string; partId: string; gameIndex: number },
) => {
  const wordsResult: FingerWritingWord[] = [];
  let startIndex = 0;

  const plainText = gameDraft.game.answer.text.text;
  const locale = gameDraft.locale;
  const strokeResolution = gameDraft.game.answer.text.strokeResolution;
  const sentences = splitIntoSentence(plainText, locale);
  sentences.forEach((sentence) => {
    const splitGraphme = splitIntoGraphme(sentence.text, locale, {
      withSpace: true,
    });

    const selectedCharacter: GameCharacter<false>[] = [];
    splitGraphme.forEach((_, index) => {
      const character = selectWords.find(
        (__, totalIndex) => index + startIndex === totalIndex,
      );
      // export type GameCharacter<B extends boolean> = {
      //   character: string;
      //   ttsAudio?: Uuid;
      //   strokeCount?: number;
      // } & (B extends true ? { fileId: Uuid } : { fileId?: Uuid });

      if (character) {
        const strokeData = strokeResolution?.[character.key];

        if (strokeData && character.type === TextOrderingUnitType.Blank) {
          selectedCharacter.push({
            character: character.key,
            strokeCount: strokeData.strokeCount,
            fileId: strokeData.fileId,
          });
        } else if (strokeData) {
          selectedCharacter.push({
            character: character.key,
            fileId: strokeData.fileId,
          });
        } else {
          selectedCharacter.push({
            character: character.key,
          });
        }
      }
    });
    startIndex = startIndex + splitGraphme.length;

    wordsResult.push({ word: sentence.text, characters: selectedCharacter });
  });

  return generateFingerWritingProduct(
    gameDraft,
    wordsResult,
    {
      draftId,
      partId,
      index: gameIndex,
    },
    { useComponent: true, isForChallenge: false },
  );
};
